import type { ReactNode } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import type { Reservoir } from "@/lib/api/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  FormField,
  emptyToUndef as _emptyToUndef,
  numToStr,
  parseOptNum as _parseOptNum,
} from "@/components/forms/field";

// Shared schema for Reservoir create + update. Same string-typed numerics
// pattern as GrowForm — the resolver only validates "looks like a number";
// parseOptNum at submit time produces the actual number | undefined.

const optionalNumString = z
  .string()
  .optional()
  .refine(
    (v) => v === undefined || v === "" || !Number.isNaN(Number(v)),
    "must be a number",
  );

export const reservoirSchema = z.object({
  name: z.string().min(1, "name is required"),
  container_type: z.string().min(1, "container_type is required"),
  inside_l_in: optionalNumString,
  inside_w_in: optionalNumString,
  inside_h_in: optionalNumString,
  cross_section_in2: optionalNumString,
  gal_per_inch: optionalNumString,
  cm_per_gal: optionalNumString,
  notes: z.string().optional(),
  reason: z.string().optional(),
});

export type ReservoirFormValues = z.infer<typeof reservoirSchema>;

// Re-exports so consumer pages have one import path. The underscored
// imports above avoid shadowing.
export const parseOptNum = _parseOptNum;
export const emptyToUndef = _emptyToUndef;

export interface ReservoirFormProps {
  initial?: Reservoir;
  submitting: boolean;
  onSubmit: (values: ReservoirFormValues) => void;
  banner?: ReactNode;
  submitLabel?: string;
  showReason?: boolean;
}

export function ReservoirForm({
  initial,
  submitting,
  onSubmit,
  banner,
  submitLabel,
  showReason = false,
}: ReservoirFormProps) {
  const form = useForm<ReservoirFormValues>({
    resolver: zodResolver(reservoirSchema),
    defaultValues: defaultsFor(initial),
  });
  const err = form.formState.errors;

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      className="space-y-6 max-w-2xl"
      noValidate
    >
      {banner}

      <div className="grid grid-cols-2 gap-4">
        <FormField label="Name" error={err.name?.message}>
          <Input autoFocus {...form.register("name")} />
        </FormField>
        <FormField
          label="Container type"
          help="tote, bucket, drum, …"
          error={err.container_type?.message}
        >
          <Input {...form.register("container_type")} />
        </FormField>
      </div>

      <fieldset className="space-y-3">
        <legend className="text-sm font-medium">Inside dimensions (in)</legend>
        <div className="grid grid-cols-3 gap-3">
          <FormField label="length" error={err.inside_l_in?.message}>
            <Input
              type="number"
              step="any"
              {...form.register("inside_l_in")}
            />
          </FormField>
          <FormField label="width" error={err.inside_w_in?.message}>
            <Input
              type="number"
              step="any"
              {...form.register("inside_w_in")}
            />
          </FormField>
          <FormField label="height" error={err.inside_h_in?.message}>
            <Input
              type="number"
              step="any"
              {...form.register("inside_h_in")}
            />
          </FormField>
        </div>
      </fieldset>

      <fieldset className="space-y-3">
        <legend className="text-sm font-medium">Volume conversion</legend>
        <div className="grid grid-cols-3 gap-3">
          <FormField
            label="cross_section_in²"
            error={err.cross_section_in2?.message}
          >
            <Input
              type="number"
              step="any"
              {...form.register("cross_section_in2")}
            />
          </FormField>
          <FormField label="gal/inch" error={err.gal_per_inch?.message}>
            <Input
              type="number"
              step="any"
              {...form.register("gal_per_inch")}
            />
          </FormField>
          <FormField label="cm/gal" error={err.cm_per_gal?.message}>
            <Input
              type="number"
              step="any"
              {...form.register("cm_per_gal")}
            />
          </FormField>
        </div>
      </fieldset>

      <FormField label="Notes">
        <Textarea rows={3} {...form.register("notes")} />
      </FormField>

      {showReason && (
        <FormField
          label="Reason for change"
          help="Captured in the audit log."
          error={err.reason?.message}
        >
          <Input {...form.register("reason")} />
        </FormField>
      )}

      <div className="flex gap-2">
        <Button type="submit" disabled={submitting}>
          {submitting ? "Saving…" : (submitLabel ?? "Save")}
        </Button>
      </div>
    </form>
  );
}

function defaultsFor(r: Reservoir | undefined): ReservoirFormValues {
  if (!r) {
    return { name: "", container_type: "", notes: "" };
  }
  return {
    name: r.name,
    container_type: r.container_type,
    inside_l_in: numToStr(r.inside_l_in),
    inside_w_in: numToStr(r.inside_w_in),
    inside_h_in: numToStr(r.inside_h_in),
    cross_section_in2: numToStr(r.cross_section_in2),
    gal_per_inch: numToStr(r.gal_per_inch),
    cm_per_gal: numToStr(r.cm_per_gal),
    notes: r.notes ?? "",
  };
}
